/**
 * rankValidation.ts - Promoter Rank Validation and Input Processing
 *
 * This utility module provides comprehensive validation logic for promoter ranking
 * systems, including rank contiguity validation, minimum donation requirements,
 * and input formatting for rank-based reward systems.
 *
 * Key Functions:
 * - parseRankString: Parses rank strings (e.g., "1", "2-5") into start/end values
 * - validateRankInput: Validates rank inputs for contiguity and format
 * - validateRankMinimum: Validates minimum donation amounts with ordering rules
 * - getExpectedStartRank: Calculates expected start rank for new entries
 * - getRankPlaceholder: Generates helpful placeholder text for inputs
 *
 * Features:
 * - Strict contiguity enforcement (no gaps in rank sequences)
 * - Overlap detection and prevention
 * - Minimum donation validation with descending order rules
 * - Edit-in-place validation with position awareness
 * - Helpful error messages and placeholder generation
 *
 * Dependencies: None (pure TypeScript validation logic)
 */

// Utility functions for rank validation logic

export interface RankValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Parses a rank string and returns the start and end rank numbers
 */
export const parseRankString = (
  rankStr: string
): { start: number; end: number } | null => {
  if (!rankStr || rankStr.trim() === "") return null;

  const rangeMatch = rankStr.match(/^(\d+)-(\d+)$/);
  const singleMatch = rankStr.match(/^(\d+)$/);

  if (singleMatch) {
    const rank = parseInt(singleMatch[1]);
    return { start: rank, end: rank };
  } else if (rangeMatch) {
    const start = parseInt(rangeMatch[1]);
    const end = parseInt(rangeMatch[2]);
    return { start, end };
  }

  return null;
};

/**
 * Gets all occupied rank numbers from existing ranks
 */
export const getOccupiedRanks = (
  existingRanks: string[],
  excludeRank?: string
): number[] => {
  const occupiedRanks: number[] = [];

  existingRanks
    .filter((rankStr) => rankStr !== excludeRank)
    .forEach((rankStr) => {
      const parsed = parseRankString(rankStr);
      if (parsed) {
        for (let i = parsed.start; i <= parsed.end; i++) {
          occupiedRanks.push(i);
        }
      }
    });

  return occupiedRanks.sort((a, b) => a - b);
};

/**
 * Finds the expected start rank for a rank based on existing ranks and edit context
 */
export const getExpectedStartRank = (
  existingRanks: string[],
  excludeRank?: string,
  isEditing: boolean = false,
  editPosition?: number
): number => {
  // If editing the first rank (position 0), it should always start with 1
  if (isEditing && editPosition === 0) {
    return 1;
  }

  // If editing a rank that's not the first, find the previous rank's end value
  if (isEditing && editPosition !== undefined && editPosition > 0) {
    // Get all ranks except the one being edited, sorted by their original position
    const otherRanks = existingRanks
      .filter((rankStr) => rankStr !== excludeRank)
      .map((rankStr) => parseRankString(rankStr))
      .filter(Boolean)
      .sort((a, b) => a!.start - b!.start);

    // Find the rank that comes immediately before this position
    if (editPosition > 0 && otherRanks[editPosition - 1]) {
      return (otherRanks[editPosition - 1]?.end ?? 0) + 1;
    }
  }

  // For new ranks, use the previous logic
  const occupiedRanks = getOccupiedRanks(existingRanks, excludeRank);

  if (occupiedRanks.length === 0) {
    return 1; // First rank starts at 1
  }

  // Find the highest occupied rank and add 1
  const highestRank = Math.max(...occupiedRanks);
  return highestRank + 1;
};

/**
 * Validates a rank input string for contiguity and format
 */
export const validateRankInput = (
  rankInput: string,
  existingRanks: string[],
  currentRank?: string,
  isEditing: boolean = false,
  editPosition?: number,
  allRanks?: Array<{ rank: string; [key: string]: any }>
): RankValidationResult => {
  if (!rankInput || rankInput.trim() === "") {
    return { isValid: false, errorMessage: "Rank is required" };
  }

  // Parse the input
  const parsed = parseRankString(rankInput);
  if (!parsed) {
    return {
      isValid: false,
      errorMessage: 'Invalid format (use "1" or "2-5")',
    };
  }

  const { start, end } = parsed;

  // Validate range format
  if (start >= end && rankInput.includes("-")) {
    return {
      isValid: false,
      errorMessage: "End rank must be greater than start",
    };
  }

  // If editing and we have position and all ranks data, enforce strict contiguity
  if (isEditing && editPosition !== undefined && allRanks) {
    const isLastRow = editPosition === allRanks.length - 1;

    // Get previous rank (if exists)
    const previousRank = editPosition > 0 ? allRanks[editPosition - 1] : null;
    const previousParsed = previousRank
      ? parseRankString(previousRank.rank)
      : null;

    // Get next rank (if exists and not last row)
    const nextRank =
      !isLastRow && editPosition < allRanks.length - 1
        ? allRanks[editPosition + 1]
        : null;
    const nextParsed = nextRank ? parseRankString(nextRank.rank) : null;

    // For first row, must start with 1
    if (editPosition === 0 && start !== 1) {
      return { isValid: false, errorMessage: "First rank must start with 1" };
    }

    // For non-first rows, must start exactly after previous rank ends
    if (
      editPosition > 0 &&
      previousParsed &&
      start !== previousParsed.end + 1
    ) {
      return {
        isValid: false,
        errorMessage: `Must start with ${previousParsed.end + 1}`,
      };
    }

    // For non-last rows, must end exactly before next rank starts
    if (!isLastRow && nextParsed && end !== nextParsed.start - 1) {
      return {
        isValid: false,
        errorMessage: `Must end with ${nextParsed.start - 1}`,
      };
    }

    return { isValid: true };
  }

  // Get expected start rank
  const expectedStart = getExpectedStartRank(
    existingRanks,
    currentRank,
    isEditing,
    editPosition
  );

  // Check if start rank is correct
  if (start !== expectedStart) {
    return {
      isValid: false,
      errorMessage: `Must start with rank ${expectedStart}`,
    };
  }

  // Check for overlaps with existing ranks
  const occupiedRanks = getOccupiedRanks(existingRanks, currentRank);
  const inputRanks: number[] = [];
  for (let i = start; i <= end; i++) {
    inputRanks.push(i);
  }

  const hasOverlap = inputRanks.some((rank) => occupiedRanks.includes(rank));
  if (hasOverlap) {
    return {
      isValid: false,
      errorMessage: "Overlaps with existing ranks",
    };
  }

  return { isValid: true };
};

/**
 * Generates a placeholder text for rank input based on existing ranks
 */
export const getRankPlaceholder = (
  existingRanks: string[],
  currentRank?: string,
  isEditing: boolean = false,
  editPosition?: number,
  allRanks?: Array<{ rank: string; [key: string]: any }>
): string => {
  // If editing and we have position and all ranks data, use strict contiguity rules
  if (isEditing && editPosition !== undefined && allRanks) {
    const isLastRow = editPosition === allRanks.length - 1;

    // For first row, always show "1"
    if (editPosition === 0) {
      return "1";
    }

    // For non-first rows, calculate based on previous rank
    const previousRank = allRanks[editPosition - 1];
    const previousParsed = parseRankString(previousRank.rank);

    if (previousParsed) {
      const expectedStart = previousParsed.end + 1;

      // For last row, just show the expected start
      if (isLastRow) {
        return expectedStart.toString();
      }

      // For non-last rows, calculate the exact range required
      const nextRank = allRanks[editPosition + 1];
      const nextParsed = parseRankString(nextRank.rank);

      if (nextParsed) {
        const expectedEnd = nextParsed.start - 1;

        // If start and end are the same, show single number
        if (expectedStart === expectedEnd) {
          return expectedStart.toString();
        }

        // Otherwise show the required range
        return `${expectedStart}-${expectedEnd}`;
      }
    }
  }

  // Fallback to original logic
  const expectedStart = getExpectedStartRank(
    existingRanks,
    currentRank,
    isEditing,
    editPosition
  );

  if (expectedStart === 1) {
    return "1";
  }

  // Suggest both single rank and range options
  return `${expectedStart}`;
};

/**
 * Validates minimum donation amount for rank-based rewards
 * Ensures minimums are in descending order (higher ranks have higher minimums)
 * All minimums must be >= $1
 * Treats empty values as 1
 */
export const validateRankMinimum = (
  minimumInput: string,
  currentRankStr: string,
  allRanks: Array<{ rank: string; minimum?: number }>,
  editPosition?: number
): RankValidationResult => {
  // Treat empty input as 1
  const amount =
    minimumInput && minimumInput.trim() !== ""
      ? parseInt(minimumInput.replace(/[^0-9]/g, "")) || 1
      : 1;

  if (amount < 1) {
    return { isValid: false, errorMessage: "Min $1" };
  }

  // Parse current rank to get its numerical value
  const currentParsed = parseRankString(currentRankStr);
  if (!currentParsed) {
    return { isValid: true }; // Can't validate position without valid rank
  }

  // Check against higher priority ranks (lower numerical rank values)
  for (const otherRank of allRanks) {
    if (!otherRank || otherRank.rank === currentRankStr) continue;

    // Parse the other rank to get its numerical value
    const otherParsed = parseRankString(otherRank.rank);
    if (!otherParsed) continue;

    // Treat undefined/null minimum as 1
    const otherMinimum = otherRank.minimum || 1;

    // If this is a higher priority rank (lower numerical rank value)
    // For example: rank "1" has higher priority than rank "2"
    // For ranges: rank "1-3" has higher priority than rank "4-5"
    if (otherParsed.start < currentParsed.start) {
      // Current rank's minimum cannot be greater than higher rank's minimum
      if (amount > otherMinimum) {
        return {
          isValid: false,
          errorMessage: `Max $${otherMinimum.toLocaleString()}`,
        };
      }
    }
  }

  return { isValid: true };
};

/**
 * Gets the next available rank string for a new rank
 */
export const getNextAvailableRank = (existingRanks: string[]): string => {
  const expectedStart = getExpectedStartRank(existingRanks);
  return expectedStart.toString();
};

/**
 * Formats currency for display in validation messages
 */
export const formatCurrencyForValidation = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};
